import type { ReactElement } from 'react';
import { makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import { MailArrowUp20Regular } from '@fluentui/react-icons';
import { formatMailDate } from '@/utils/mailDate';
import type { SelectedItemInfo } from '@/services/office/selection';

/** Au-delà, on annonce le reste en nombre plutôt que d'allonger l'infobulle. */
const MAX_LISTED = 3;

const useStyles = makeStyles({
  // Surface AUX COULEURS DE LA MARQUE, et non la surface neutre par défaut de
  // Fluent. Ce n'est pas de la décoration : cette infobulle ne décrit pas un
  // libellé tronqué, elle annonce une ACTION et son objet — « voici le mail qui
  // va partir dans ce dossier ». Le bleu la distingue au premier regard des
  // infobulles d'information de la même page (nom complet d'une colonne, noms de
  // pièces jointes), qui restent neutres.
  surface: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    // Fluent pose une bordure neutre qui trancherait sur le fond bleu.
    border: 'none',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    maxWidth: '300px',
  },
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  // L'icône est CELLE DU BOUTON survolé : elle relie l'infobulle au geste qui
  // l'a fait paraître, là où un titre seul flotte au-dessus de la ligne.
  headIcon: { flexShrink: 0, fontSize: '20px' },
  item: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  from: { fontWeight: tokens.fontWeightSemibold, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  subject: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // Blanc atténué et non `colorNeutralForeground3` : sur fond de marque, le gris
  // des textes secondaires du thème devient illisible (contraste ~1,5).
  dim: { opacity: 0.85, fontSize: tokens.fontSizeBase200 },
  // Sépare visuellement les mails les uns des autres sans tracer de filet.
  more: { opacity: 0.85, fontSize: tokens.fontSizeBase200, fontStyle: 'italic' },
});

interface Props {
  /** Sélection Outlook courante — jamais vide quand le bouton est affiché. */
  items: readonly SelectedItemInfo[];
  /** Le déclencheur : bouton de rangement rapide, ou entrée équivalente. */
  children: ReactElement;
}

/**
 * Infobulle du bouton « Ranger ici » : elle nomme le ou les mails qui vont
 * réellement partir.
 *
 * Composant PARTAGÉ, et il a de bonnes raisons de l'être : le même geste existe
 * dans l'arbre (`FolderRow`) et dans la page détail (`FolderDetail`), où le même
 * balisage était recopié à l'identique — deux copies d'un contenu qui, lui, doit
 * absolument dire la même chose des deux côtés. La sélection a pu être faite
 * plusieurs écrans plus tôt, et le rangement est un déplacement : « Ranger 1
 * mail(s) ici » ne disait pas QUEL mail allait bouger.
 */
export function FileHereTooltip({ items, children }: Props) {
  const styles = useStyles();
  const listed = items.slice(0, MAX_LISTED);
  const rest = items.length - listed.length;

  const content = (
    <div className={styles.body}>
      <span className={styles.head}>
        <MailArrowUp20Regular className={styles.headIcon} />
        {items.length === 1 ? 'Ranger ce mail ici' : `Ranger ${items.length} mails ici`}
      </span>
      {listed.map((item) => (
        // `itemId` est unique dans la sélection : c'est l'identifiant Office de
        // l'élément, pas son objet (deux mails peuvent partager le même objet).
        <span key={item.itemId} className={styles.item}>
          {(item.fromName || item.fromAddress) && (
            <span className={styles.from}>{item.fromName || item.fromAddress}</span>
          )}
          <span className={styles.subject}>{item.subject || '(sans objet)'}</span>
          {/* La date n'est portée que sur une sélection d'un seul mail : à
              plusieurs, elle triple la hauteur de l'infobulle pour une
              information dont personne ne se sert à ce moment-là. */}
          {items.length === 1 && item.sentAt && (
            <span className={mergeClasses(styles.subject, styles.dim)}>
              {formatMailDate(item.sentAt)}
            </span>
          )}
        </span>
      ))}
      {rest > 0 && (
        <span className={styles.more}>
          {rest === 1 ? '+ 1 autre mail' : `+ ${rest} autres mails`}
        </span>
      )}
    </div>
  );

  return (
    // `relationship="label"` : le contenu est du JSX, donc inexploitable comme
    // étiquette accessible — chaque appelant garde son propre `aria-label` sur le
    // bouton.
    //
    // La classe passe par le SLOT `content` et non par un `className` sur le
    // Tooltip : chez Fluent, `content` EST la surface de l'infobulle (le composant
    // n'a pas de racine à lui, son enfant est le déclencheur), et `className` y est
    // d'ailleurs refusé par le typage.
    <Tooltip content={{ children: content, className: styles.surface }} relationship="label">
      {children}
    </Tooltip>
  );
}
